import React, { useState, useEffect } from 'react';
import * as api from '../api/api';
import { Box, Switch, FormControlLabel, Paper, Typography, IconButton, Tooltip, Button, Select, MenuItem } from '@mui/material';
import { ViewCompact, ViewComfy, Brightness4 as DarkModeIcon, Brightness7 as LightModeIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useCompactMode } from '../hooks/useCompactMode';
import CAProductsHome from './CAProductsHome';
import OutcomeOverview from './OutcomeOverview';
import OutcomeWizard from './Wizard/OutcomeWizard';
import PlanningPage from './Planning/PlanningPage';
import CreateEditPlan from './Planning/CreateEditPlan';
import Roster from './Roster';


import { ForecastVsActuals } from '../features/ForecastVsActuals';
import AdminPage from './Admin/AdminPage';
import RegressionDashboard from '../features/DevTools/components/RegressionDashboard';
import PMMode from '../features/PMMode';
import CapacityPlanner from '../features/PMMode/components/CapacityPlanner/index';

function MainApp() {
  const [currentView, setCurrentView] = useState('home');
  const [activeSubModule, setActiveSubModule] = useState('docHub');
  const [editingOutcome, setEditingOutcome] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [homePlans, setHomePlans] = useState([]);
  const [homePlansLoading, setHomePlansLoading] = useState(false);
  const [selectedFvaPlanId, setSelectedFvaPlanId] = useState('');
  
  // Theme and compact mode hooks
  const theme = useTheme();
  const { compactMode, toggleCompactMode, getCompactClasses, getCompactStyles, isCompact } = useCompactMode('normal');
  
  // Theme mode toggle handler
  const toggleThemeMode = () => {
    // Dispatch event to App.js to toggle theme
    window.dispatchEvent(new CustomEvent('toggleTheme'));
  };
  
  // Planning state
  const [showPlanEditor, setShowPlanEditor] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);

  // Fetch plans once when the home view is first shown
  useEffect(() => {
    if (currentView !== 'home') return;
    if (homePlans.length > 0) return;
    setHomePlansLoading(true);
    api.getPlans()
      .then(response => {
        const plans = Array.isArray(response) ? response
          : response?.plans ?? response?.data ?? [];
        setHomePlans(plans);
        if (!selectedFvaPlanId && plans.length > 0) {
          const approved = plans
            .filter(p => ['approved', 'active', 'published'].includes((p.status || '').toLowerCase()))
            .sort((a, b) => (b.year || 0) - (a.year || 0));
          const best = approved.find(p => (p.selected_outcomes?.length || p.outcomes?.length || 0) > 0)
            || approved[0] || plans[0];
          if (best) setSelectedFvaPlanId(best.id);
        }
      })
      .catch(() => {})
      .finally(() => setHomePlansLoading(false));
  }, [currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load departments once on mount for the global filter
  useEffect(() => {
    api.getDepartments()
      .then(data => setAvailableDepartments(Array.isArray(data) ? data : (data?.departments || [])))
      .catch(() => {});
  }, []);

  // Dispatch edit mode change event when read-only state changes
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('editModeChanged', {
      detail: { isReadOnly }
    }));
  }, [isReadOnly]);

  // Navigation handler for CAProductsHome
  const handleNavigateToModule = (moduleId, subModule) => {
    console.log('Navigating to module:', moduleId, subModule);

    switch (moduleId) {
      case 'outcomes':
        setCurrentView('outcomes');
        break;
      case 'roster':
        setCurrentView('roster');
        break;
      case 'planning':
        setCurrentView('planning');
        break;
      case 'forecast':
        setCurrentView('forecast');
        break;
      case 'admin':
        setCurrentView('admin');
        break;
      case 'regression':
        setCurrentView('regression');
        break;
      case 'capacity':
        setCurrentView('capacity');
        break;
      case 'pmmode':
        setActiveSubModule(subModule || 'docHub');
        setCurrentView('pmmode');
        break;
      default:
        setCurrentView('home');
    }
  };

  // Outcome handlers
  const handleEditOutcome = (outcome) => {
    setEditingOutcome(outcome);
    setShowWizard(true);
  };

  const handleNewOutcome = () => {
    setEditingOutcome(null);
    setShowWizard(true);
  };

  const handleBackToOutcomes = () => {
    setShowWizard(false);
    setEditingOutcome(null);
    setCurrentView('outcomes');
  };

  // Planning handlers
  const handleCreatePlan = () => {
    setEditingPlanId(null);
    setShowPlanEditor(true);
  };

  const handleEditPlan = (planId) => {
    setEditingPlanId(planId);
    setShowPlanEditor(true);
  };

  const handleBackToPlanning = () => {
    setShowPlanEditor(false);
    setEditingPlanId(null);
    setCurrentView('planning');
  };

  // Back to home handler
  const handleBackToHome = () => {
    setCurrentView('home');
    setShowWizard(false);
    setShowPlanEditor(false);
    setEditingOutcome(null);
    setEditingPlanId(null);
  };

  // Listen for custom events to switch views (for forecast navigation)
  useEffect(() => {
    const handleSwitchToView = (event) => {
      const { view } = event.detail;
      setCurrentView(view);
    };

    const handleSwitchToTab = (event) => {
      const { tabIndex } = event.detail;
      // Map tab indices to views
      const tabToView = {
        0: 'outcomes',
        1: 'roster', 
        2: 'planning',
        3: 'forecast',
        4: 'admin'
      };
      const view = tabToView[tabIndex];
      if (view) {
        setCurrentView(view);
      }
    };

    window.addEventListener('switchToView', handleSwitchToView);
    window.addEventListener('switchToTab', handleSwitchToTab);
    
    return () => {
      window.removeEventListener('switchToView', handleSwitchToView);
      window.removeEventListener('switchToTab', handleSwitchToTab);
    };
  }, []);

            // Control panel with theme, compact mode, and read-only toggles
  const ControlPanel = () => {
    const getCompactIcon = () => {
      switch (compactMode) {
        case 'compact':
          return <ViewCompact />;
        default:
          return <ViewComfy />;
      }
    };

    const getCompactTooltip = () => {
      switch (compactMode) {
        case 'compact':
          return 'Compact view - More information per screen';
        default:
          return 'Normal view - Standard spacing';
      }
    };

    return (
      <Paper 
        elevation={1} 
        sx={{ 
          position: 'fixed', 
          top: 56, // Adjusted for smaller app bar
          right: 12, 
          p: 1, // Reduced padding
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: 1, // Gap between controls
          borderRadius: 2
        }}
      >
        {/* Theme Mode Toggle */}
        <Tooltip title={`Switch to ${theme.palette.mode === 'dark' ? 'light' : 'dark'} mode`}>
          <IconButton 
            onClick={toggleThemeMode} 
            size="small"
            color="inherit"
          >
            {theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>
        
        {/* Divider */}
        <Box sx={{ width: '1px', height: '20px', bgcolor: 'divider', mx: 0.5 }} />
        
        {/* Compact Mode Toggle */}
        <Tooltip title={getCompactTooltip()}>
          <IconButton 
            onClick={toggleCompactMode} 
            size="small"
            color={compactMode !== 'normal' ? 'primary' : 'default'}
          >
            {getCompactIcon()}
          </IconButton>
        </Tooltip>
        
        {/* Divider */}
        <Box sx={{ width: '1px', height: '20px', bgcolor: 'divider', mx: 0.5 }} />

        {/* Department Filter */}
        <Select
          size="small"
          value={selectedDepartment}
          onChange={e => setSelectedDepartment(e.target.value)}
          displayEmpty
          sx={{ fontSize: '0.75rem', minWidth: 110, height: 28, '& .MuiSelect-select': { py: '3px' } }}
        >
          <MenuItem value="" sx={{ fontSize: '0.75rem' }}><em>All Depts</em></MenuItem>
          {availableDepartments.map(d => (
            <MenuItem key={d.id} value={d.name} sx={{ fontSize: '0.75rem' }}>{d.name}</MenuItem>
          ))}
        </Select>

        {/* Divider */}
        <Box sx={{ width: '1px', height: '20px', bgcolor: 'divider', mx: 0.5 }} />

        {/* Read-Only Toggle */}
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          Mode:
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={!isReadOnly}
              onChange={(e) => setIsReadOnly(!e.target.checked)}
              color="primary"
              size="small" // Smaller switch
            />
          }
          label={isReadOnly ? "Read-Only" : "Edit"}
          sx={{ 
            m: 0, 
            '& .MuiFormControlLabel-label': { 
              fontSize: '0.75rem' // Smaller label
            }
          }}
        />
      </Paper>
    );
  };

    return (
    <Box className={getCompactClasses()} sx={getCompactStyles()}>
      {/* Control panel with read-only and compact mode toggles */}
      <ControlPanel />
      
      {/* CA Products Home - Landing Page */}
      {currentView === 'home' && (
        <CAProductsHome
          onNavigateToModule={handleNavigateToModule}
          isReadOnly={isReadOnly}
          isCompact={isCompact}
          availablePlans={homePlans}
          plansLoading={homePlansLoading}
          selectedFvaPlanId={selectedFvaPlanId}
          onFvaPlanChange={setSelectedFvaPlanId}
        />
      )}
      
      {/* Outcomes Module */}
      {currentView === 'outcomes' && !showWizard && (
        <OutcomeOverview
          onEditOutcome={handleEditOutcome}
          onNewOutcome={handleNewOutcome}
          onViewOutcome={handleEditOutcome}
          onBackToHome={handleBackToHome}
          isReadOnly={isReadOnly}
          selectedDepartment={selectedDepartment}
        />
      )}
      {currentView === 'outcomes' && showWizard && (
        <OutcomeWizard
          selectedOutcome={editingOutcome}
          onBack={handleBackToOutcomes}
          readOnly={isReadOnly}
        />
      )}
      
      {/* Planning Module */}
      {currentView === 'planning' && !showPlanEditor && (
        <PlanningPage
          onCreatePlan={handleCreatePlan}
          onEditPlan={handleEditPlan}
          onBackToHome={handleBackToHome}
          isReadOnly={isReadOnly}
          selectedDepartment={selectedDepartment}
        />
      )}
      {currentView === 'planning' && showPlanEditor && (
        <CreateEditPlan
          planId={editingPlanId}
          onClose={handleBackToPlanning}
          isReadOnly={isReadOnly}
        />
      )}
      
                              {/* Roster Module */}
      {currentView === 'roster' && (
        <Box key="roster-module">
          <Roster
            onBackToHome={handleBackToHome}
            isReadOnly={isReadOnly}
            isActive={currentView === 'roster'}
            selectedDepartment={selectedDepartment}
          />
        </Box>
      )}
      
            {/* Forecast Module */}
      {currentView === 'forecast' && (
        <Box key="forecast-module">
          <ForecastVsActuals
            onBackToHome={handleBackToHome}
            isReadOnly={isReadOnly}
            isCompact={isCompact}
            initialPlanId={selectedFvaPlanId || undefined}
            availablePlans={homePlans.length > 0 ? homePlans : undefined}
            selectedDepartment={selectedDepartment}
          />
        </Box>
      )}
      
            {/* Admin Module */}
      {currentView === 'admin' && (
        <Box>
          <AdminPage
            onBackToHome={handleBackToHome}
            isReadOnly={isReadOnly}
          />
        </Box>
      )}

      {/* Regression Dashboard */}
      {currentView === 'regression' && (
        <Box>
          <RegressionDashboard onBackToHome={handleBackToHome} />
        </Box>
      )}

      {/* Capacity Tracker */}
      {currentView === 'capacity' && (
        <Box>
          <CapacityPlanner
            onBackToHome={handleBackToHome}
            isReadOnly={isReadOnly}
          />
        </Box>
      )}

      {/* PM Mode */}
      {currentView === 'pmmode' && (
        <Box>
          <PMMode
            onBackToHome={handleBackToHome}
            isReadOnly={isReadOnly}
            isCompact={isCompact}
            activeSubModule={activeSubModule}
          />
        </Box>
      )}

    </Box>
  );
}

export default MainApp;